import { Build, Component, Element, Host, State, h } from '@stencil/core';
import { HTMLStencilElement, Listen } from '@stencil/core/internal';
import { store } from '@stencil/redux';
import type { DIALOG_TYPES, SfcDialogCustomEvent } from '@sfc/components';
import {
  Constants,
  DisplayType,
  Language,
  SigningMethod,
  SourceName,
} from '../../enums/const-enums';
import { switchLanguage } from '../../redux/actions/config-actions';
import { RootState, initialStore } from '../../redux/store';
import { SignCancel } from '../../service/sign-cancel';
import { isDarkMode } from '../../utils/theming';
import {
  handleContinueResponse,
  isLogin,
  logd,
  setCookie,
} from '../../utils/utils';
import {
  RassHeaderTranslations,
  rassHeaderTranslations,
} from './i18n/rass-header.i18n';

@Component({
  tag: 'rass-header',
  styleUrl: 'rass-header.scss',
  shadow: true,
})
export class RassHeaderComponent {
  @Element() element!: HTMLStencilElement;
  @State() assetsPath =
    '/s-t-a-t-i-c/msp/authentication/v2/vrs_4820747/assets/';
  @State() language: Language;
  @State() sourceName: SourceName;
  @State() activeSignMethod: SigningMethod;
  @State() absPath: string;
  @State() displayType: string;
  switchLanguage!: typeof switchLanguage;
  // Use this property in case the cancel button needs to be hidden and updating the OFC(DisplayType.HEADER_WITHOUT_CANCEL_BUTTON) is not possible.
  txSitesWithoutCancelButton = [
    '/api/paymentssavings/orders/signrequest/summary.do',
    '/api/msp/mortgages/interest-renewals/signing-requests',
    '/api/msp/payments-savings/orders',
  ];
  // Use this property in case the signing is rendered within a modal that has it's own header and updating the OFC(DisplayType.SCREEN_WITHOUT_HEADER) is not possible.
  iframeParentWithoutHeader = [
    '/business/overview',
    '/business/service/security/access-and-signing/change-access-code',
    '/online/nl/profiel/toegang-en-ondertekenen',
    '/online/nl/pre-registratie/voorwaarden',
  ];
  readonly hasLoadedFallbackMap: Map<string, boolean> = new Map();

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      switchLanguage,
    });
    store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName, absPath, displayType },
        methods: { activeSignMethod },
      } = state;
      return {
        assetsPath,
        language,
        sourceName,
        absPath,
        displayType,
        activeSignMethod,
      };
    });
  }

  componentDidLoad(): void {
    // Add an aria label to the body if we are inside an RBO / RBB dialog
    if (this.sourceName === SourceName.OTHER && window.self !== window.top) {
      const bodyElement = document.querySelector('body');
      bodyElement.setAttribute('aria-label', this.translations.body.ariaLabel);
    }
  }

  presentDialog(): void {
    this.element.shadowRoot
      .querySelector<HTMLSfcDialogElement>('#cancel-dialog')
      .present()
      .catch((err: Error) => logd(() => console.error(err)));
  }

  dismissDialog(): void {
    this.element.shadowRoot
      .querySelector<HTMLSfcDialogElement>('#cancel-dialog')
      .dismiss()
      .catch((err: Error) => logd(() => console.error(err)));
  }

  onLanguageDropdown(): void {
    const language =
      this.element.shadowRoot.querySelector<HTMLSfcSelectElement>(
        '#language-dropdown',
      ).value;
    setCookie(Constants.LANGUAGE_COOKIE_NAME, language);
    this.switchLanguage(language);
  }

  onLanguageToggle(language: Language): void {
    setCookie(Constants.LANGUAGE_COOKIE_NAME, language);
    this.switchLanguage(language);
  }

  get isEotp(): boolean {
    return this.activeSignMethod === SigningMethod.EOTP;
  }

  get isCancelButtonHidden(): boolean {
    return (
      isLogin() ||
      (this.isLoadedInRabobankIframe && this.isCancelButtonHiddenForTxsite)
    );
  }

  get isCancelButtonHiddenForTxsite(): boolean {
    return (
      this.displayType === DisplayType.HEADER_WITHOUT_CANCEL_BUTTON ||
      this.txSitesWithoutCancelButton.some((txSite) =>
        this.absPath?.includes(txSite),
      )
    );
  }

  get isLoadedInRabobankIframe(): boolean {
    //SourceName OTHER is only applied to the pages within Rabobank.
    return (
      this.sourceName === SourceName.OTHER && top.location !== self.location
    );
  }

  get href(): string {
    if (Build.isDev) {
      return 'http://localhost:3333/api/msp/rabobank-stub-homepage';
    } else if (location.hostname === 'banking.rabobank.com') {
      return 'https://www.rabobank.com';
    } else if (location.pathname.startsWith('/business')) {
      return 'https://www.rabobank.nl/bedrijven/';
    }
    return 'https://www.rabobank.nl/particulieren/';
  }

  get logo(): string {
    if (
      this.sourceName === SourceName.IDEAL ||
      this.sourceName === SourceName.IDIN ||
      this.sourceName === SourceName.WERO
    ) {
      return `${this.assetsPath}images/${this.sourceName}.svg`;
    }
    return undefined;
  }

  get translations(): RassHeaderTranslations {
    return rassHeaderTranslations[this.language] || rassHeaderTranslations.en;
  }

  get isHeaderHidden(): boolean {
    return (
      this.displayType === DisplayType.SCREEN_WITHOUT_HEADER ||
      this.hiddenInIframe
    );
  }

  get hiddenInIframe(): boolean {
    try {
      // Use a try...catch block because cross-origin usage of top.location will throw an err
      if (
        window.self !== window.top &&
        this.iframeParentWithoutHeader.includes(window.top.location.pathname)
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  get imageThemeFragment(): string {
    return isDarkMode() ? 'dm' : 'lm';
  }

  @Listen('message', { target: 'window' })
  onMessage(event: MessageEvent<AppModel.SensesSignerMessageData>) {
    if (event.data?.action) {
      switch (event.data.action) {
        case 'cancel-dialog':
          this.presentDialog();
          break;
        case 'cancel':
          this.cancelSigning();
          break;
      }
    }
  }

  onCancelDialogDismiss(
    event: SfcDialogCustomEvent<{ dimissed: boolean; confirmed: boolean }>,
  ): void {
    if (event.detail.confirmed) {
      this.cancelSigning();
    }
  }

  cancelSigning(): void {
    new SignCancel()
      .doSignCancel()
      .then((resp) => {
        handleContinueResponse(resp, 'canceled');
      })
      .catch(() => {
        this.dismissDialog();
      });
  }

  render(): void {
    return (
      <Host>
        <header class={this.isHeaderHidden ? 'sfc-display-none' : ''}>
          <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
          <div class="section-header sfc-container-fluid">
            <div class="sfc-row sfc-justify-content-between">
              <div
                class={`${isLogin() ? 'sfc-col-4' : 'sfc-col-2'} sfc-align-self-center`}
              >
                <a
                  class="sfc-display-none sfc-display-md-block"
                  href={this.href}
                  target="_self"
                  rel="noopener noreferrer"
                >
                  <img
                    id="rabobank-logo"
                    src={`${this.assetsPath}images/rabobank-${this.imageThemeFragment}.svg`}
                    alt="Rabobank"
                  />
                </a>
              </div>

              <div
                class={`${isLogin() ? 'sfc-col-4' : 'sfc-col-8'} sfc-align-self-center`}
              >
                <h1
                  id="page-title"
                  class="sfc-heading-6 sfc-font-text-align-center sfc-margin-block-end-0"
                  aria-label={this.translations.title[this.sourceName]}
                >
                  {this.translations.title[this.sourceName]}
                  {this.logo ? (
                    <img
                      id="source-name-logo"
                      class="sfc-padding-inline-start-50"
                      src={this.logo}
                      alt=""
                    />
                  ) : (
                    ''
                  )}
                </h1>
              </div>

              <div
                class={`${isLogin() ? 'sfc-col-4' : 'sfc-col-2'} sfc-align-self-center`}
              >
                <div
                  class={
                    isLogin() && this.isEotp
                      ? 'sfc-padding-inline-end-25'
                      : 'sfc-display-none'
                  }
                >
                  <sfc-select
                    id="language-dropdown"
                    class="language-selector sfc-display-inline-block"
                    value={this.language}
                    required
                    onSfcChange={(): void => this.onLanguageDropdown()}
                  >
                    <sfc-option value={Language.NL}>NL</sfc-option>
                    <sfc-option value={Language.EN}>EN</sfc-option>
                    <sfc-option value={Language.ES}>ES</sfc-option>
                    <sfc-option value={Language.PT}>PT</sfc-option>
                  </sfc-select>
                </div>

                <div
                  id="language-toggle"
                  class={
                    isLogin() && !this.isEotp
                      ? 'language-selector notranslate sfc-display-none sfc-display-sm-block'
                      : 'sfc-display-none'
                  }
                >
                  <sfc-button
                    id="langNL"
                    class="sfc-display-inline-block"
                    variant="text"
                    disabled={this.language === Language.NL}
                    onSfcClick={(): void => this.onLanguageToggle(Language.NL)}
                  >
                    NL
                  </sfc-button>
                  <span class="sfc-text--size-md"> | </span>
                  <sfc-button
                    id="langEN"
                    class="sfc-display-inline-block"
                    variant="text"
                    disabled={this.language === Language.EN}
                    onSfcClick={(): void => this.onLanguageToggle(Language.EN)}
                  >
                    EN
                  </sfc-button>
                </div>

                <div
                  class={this.isCancelButtonHidden ? 'sfc-display-none' : ''}
                >
                  <sfc-button
                    id="cancel-button"
                    variant="text"
                    aria-label={this.translations.button.label}
                    onSfcClick={(): void => this.presentDialog()}
                  >
                    <sfc-icon name="rds-icon-times" size="sm" />
                    <span class="sfc-display-none sfc-display-md-block sfc-padding-inline-start-50">
                      {this.translations.button.label}
                    </span>
                  </sfc-button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <sfc-dialog
          id="cancel-dialog"
          type={'confirm' as DIALOG_TYPES}
          onSfcDialogDidDismiss={(
            event: SfcDialogCustomEvent<{
              dimissed: boolean;
              confirmed: boolean;
            }>,
          ): void => this.onCancelDialogDismiss(event)}
          confirmText={this.translations.modal.button.label.confirm}
          cancelText={this.translations.modal.button.label.cancel}
        >
          <span slot="header">
            {this.translations.modal.title[this.sourceName]}
          </span>
          <div slot="body">
            <div class="sfc-row sfc-margin-inline-0 sfc-margin-block-100">
              <p class="sfc-paragraph sfc-margin -block-start-100">
                {this.translations.modal.body[this.sourceName]}
              </p>
            </div>
          </div>
        </sfc-dialog>
      </Host>
    ) as void;
  }
}
